import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import classNames from "classnames";
import { nanoid } from "nanoid";
import { useOnClickOutside } from "usehooks-ts";
import style from "./rule-cell.module.css";
import {
  ElementRule,
  ElementRuleComparator,
  ElementRuleValueType,
  ruleSchema,
} from "shared/datamodel/schemas/element-data-layer";
import { RuleCategorizedDropdown, RuleEditableDropdown, RuleEditableText, RuleIconPicker } from "./rule-editable-value";
import { getElementTypeForId } from "frontend/canvas-designer-new/elements/canvas-elements-utils";
import {
  getPropertiesForElement,
  getPropertyOptions,
  getPropertyTitleForElement,
  getPropertyTypeForElement,
} from "./rule-creator-utils";
import consts from "shared/consts";
import { useDeepEqualMemo } from "frontend/hooks/use-deep-memoize";
import { Floater } from "frontend/ui-components/floaters/floater";
import ThreeDotsIcon from "frontend/ui-components/design-system/icons/three-dots-icon";
import { PortalID } from "frontend/modal/portal-types";
import ReactDOM from "react-dom";
import { useDynamicIntegrations } from "../../../hooks/use-dynamic-integrations";
import { CanvasElement } from "shared/datamodel/schemas";
import tracking from "frontend/tracking";
import { MondayLogo } from "frontend/icons/monday-logo";
import { MissingDataIcon } from "../../../icons/missing-data-icon";
import { UNSUPPORTED_COLUMNS } from "../../../consts";
import { ColorPicker } from "frontend/canvas-designer-new/elements-toolbar-cim-restyle/widgets/color-picker";
import { Icon } from "frontend/canvas-designer-new/elements-toolbar-cim-restyle/toolbar-buttons";

interface ElementDynamicRuleCellProps {
  elementId: string;
  element: CanvasElement;
  rule?: ElementRule;
  newRuleId?: string;
  onChange: (rule: ElementRule) => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  isPlaceholder?: boolean;
}

interface RuleEditorSubmenuOption {
  title: string;
  icon: React.ReactNode;
  id: string;
  onClick: () => void;
}

export default function ElementDynamicRuleCell({
  elementId,
  element,
  rule,
  newRuleId,
  onChange,
  onDelete,
  onDuplicate,
  isPlaceholder = false,
}: ElementDynamicRuleCellProps) {
  const { isLoading, fieldIds, integrationsConfig, integrationItemIds, getField, getFieldValue } =
    useDynamicIntegrations(elementId, element);

  const [fieldId, setFieldId] = useState<string | undefined>(rule?.fieldId);
  const [comparator, setComparator] = useState<ElementRuleComparator | undefined>(rule?.comparator);
  const [value, setValue] = useState<string>(rule?.compareValue ?? "");
  const [actionProperty, setActionProperty] = useState<string | undefined>(rule?.action.property);
  const [actionValue, setActionValue] = useState<string | undefined>(rule?.action.value);
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  const [itemId, setItemId] = useState<string | undefined>(rule?.itemId);
  const isActive = rule?.active ?? true;

  useEffect(() => {
    if (
      itemId !== rule?.itemId ||
      fieldId !== rule?.fieldId ||
      comparator !== rule?.comparator ||
      value !== rule?.compareValue ||
      actionProperty !== rule?.action.property ||
      actionValue !== rule?.action.value
    ) {
      const ruleId = rule?.id ?? newRuleId ?? nanoid(10);
      const newRule = ruleSchema.safeParse({
        id: ruleId,
        itemId,
        fieldId,
        comparator,
        compareValue: value,
        action: { property: actionProperty, value: actionValue },
        active: true,
      });
      if (newRule.success) {
        onChange(newRule.data);
      }

      // Track the first property change on a rule
      const ruleCells = [fieldId, comparator, value, actionProperty, actionValue];
      const isFirstChange = rule === undefined && ruleCells.filter(Boolean).length === 1;
      if (isFirstChange) {
        tracking.trackAnalyticsEvent("data-layer-rule-creation-started", { elementId, ruleId });
      }

      // Track when a rule is finished being created
      const isLastChange = ruleCells.filter(Boolean).length === ruleCells.length;
      if (rule === undefined && isLastChange && newRule.success) {
        tracking.trackAnalyticsEvent("data-layer-rule-creation-completed", { elementId, ruleId });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId, fieldId, comparator, value, actionProperty, actionValue]);

  const memoizedRule = useDeepEqualMemo(rule);
  useEffect(() => {
    if (memoizedRule) {
      setItemId(memoizedRule.itemId);
      setFieldId(memoizedRule.fieldId);
      setComparator(memoizedRule.comparator);
      setValue(memoizedRule.compareValue);
      setActionProperty(memoizedRule.action.property);
      setActionValue(memoizedRule.action.value);
    }
  }, [memoizedRule]);

  const getFieldTitle = useCallback(
    (id: string): string => {
      if (id === "elementText") {
        return "Element text";
      }
      const field = getField(id);
      return field?.title ?? "Field";
    },
    [getField]
  );

  function getComparatorTitle(comparator: ElementRuleComparator): string {
    switch (comparator) {
      case ElementRuleComparator.eq: {
        return "equals";
      }
      case ElementRuleComparator.neq: {
        return "does not equal";
      }
      case ElementRuleComparator.gt: {
        return "greater than";
      }
      case ElementRuleComparator.gte: {
        return "greater than or equal";
      }
      case ElementRuleComparator.lt: {
        return "less than";
      }
      case ElementRuleComparator.lte: {
        return "less than or equal";
      }
      case ElementRuleComparator.contains: {
        return "contains";
      }
      case ElementRuleComparator.not_contains: {
        return "does not contain";
      }
      default: {
        return "Unknown";
      }
    }
  }

  function shouldRenderIs(comparator?: ElementRuleComparator) {
    switch (comparator) {
      case ElementRuleComparator.gt:
      case ElementRuleComparator.gte:
      case ElementRuleComparator.lt:
      case ElementRuleComparator.lte: {
        return true;
      }
      default: {
        return false;
      }
    }
  }

  function shouldRenderTo(comparator?: ElementRuleComparator) {
    switch (comparator) {
      case undefined:
      case ElementRuleComparator.gte:
      case ElementRuleComparator.lte: {
        return true;
      }
      default: {
        return false;
      }
    }
  }

  const memoizedFieldIds = useDeepEqualMemo(fieldIds);
  const memoizedIntegrationsConfig = useDeepEqualMemo(integrationsConfig);

  const { fieldOptions, fieldCategories } = useMemo(() => {
    const fieldCategories: Record<string, any> = {
      local: { title: "SHAPE PROPERTIES" },
    };
    const integrationFieldOptions = [];
    for (const integrationId in memoizedIntegrationsConfig) {
      const integration = memoizedIntegrationsConfig[integrationId];
      if (!integration?.columns) {
        continue;
      }

      for (const column of integration.columns) {
        if (column.type && UNSUPPORTED_COLUMNS.has(column.type)) {
          continue;
        }
        const itemsIds = integrationItemIds[integrationId];
        if (!itemsIds) {
          continue;
        }
        for (const itemId of itemsIds) {
          integrationFieldOptions.push({
            category: `integrations:${itemId}`,
            option: `integration:${integrationId}:${itemId}:${column.id}`,
          });
          fieldCategories[`integrations:${itemId}`] = {
            title: getFieldValue("integration", integrationId, itemId, "name"),
            icon: <MondayLogo size={16} />,
          };
        }
      }
    }

    const elementTextFieldOption = { category: "local", option: "elementText" };

    // We do not suport custom fields currently but we will in the future
    const customFieldOptions = memoizedFieldIds.map((fieldId) => ({
      category: "local",
      option: fieldId,
    }));

    return {
      fieldOptions: [elementTextFieldOption, ...customFieldOptions, ...integrationFieldOptions],
      fieldCategories,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoizedFieldIds, memoizedIntegrationsConfig]);

  const missingField = useMemo(() => {
    if (isLoading) return false;
    if (!fieldId) return false;
    return !fieldOptions.some((option) => option.option === fieldId);
  }, [isLoading, fieldId, fieldOptions]);

  const actionProperties = useMemo(() => {
    const elementType = getElementTypeForId(elementId);
    return getPropertiesForElement(elementType);
  }, [elementId]);

  const getActionPropertyTitle = useCallback((property: string) => {
    return getPropertyTitleForElement(property);
  }, []);

  const menuOptions = useMemo(() => {
    const options = [];
    if (rule !== undefined) {
      options.push({
        title: isActive ? "Disable" : "Enable",
        icon: isActive ? <Icon icon="icon-eye-open" /> : <Icon icon="icon-eye-closed" />,
        id: "toggle",
        onClick: () => {
          if (!rule) return;
          onChange({ ...rule, active: !isActive });
          setShowMenu(false);
        },
      });
    }

    if (onDuplicate) {
      options.push({
        title: "Duplicate",
        icon: <Icon icon="icon-duplicate" />,
        id: "duplicate",
        onClick: () => {
          onDuplicate();
          setShowMenu(false);
        },
      } as RuleEditorSubmenuOption);
    }

    if (onDelete) {
      options.push({
        title: "Delete",
        icon: <Icon icon="icon-trash-large" />,
        id: "delete",
        onClick: () => {
          onDelete();
          setShowMenu(false);
        },
      } as RuleEditorSubmenuOption);
    }
    return options.length > 0 ? options : null; // Show menu options only if not empty
  }, [isActive, onChange, onDelete, onDuplicate]);

  function renderActionValueInput() {
    if (!actionProperty) {
      return null;
    }
    const elementType = getElementTypeForId(elementId);
    const actionValueType = getPropertyTypeForElement(actionProperty);
    switch (actionValueType) {
      case ElementRuleValueType.color: {
        const options = getPropertyOptions(elementType, actionProperty);
        return (
          <>
            <span>to</span>
            <div className={style.color} style={{ userSelect: isActive ? "inherit" : "none" }}>
              <ColorPicker
                colorPalette={options}
                onChange={setActionValue}
                value={actionValue}
                multi={false}
                theme="dark"
                boundary="stage-container"
                allowCustomColor={elementType === consts.CANVAS_ELEMENTS.SHAPE}
                customColors={[]}
                tooltip=""
                portalId={PortalID.Floaters}
                editable={isActive}
              />
            </div>
          </>
        );
      }
      case ElementRuleValueType.text: {
        return (
          <>
            <span>to</span>
            <RuleEditableText
              value={actionValue}
              placeholder="Value"
              name="property-value"
              onChange={setActionValue}
              editable={isActive}
            />
          </>
        );
      }
      case ElementRuleValueType.icon: {
        return (
          <>
            <span>to</span>
            <RuleIconPicker value={actionValue} onChange={setActionValue} editable={isActive} />
          </>
        );
      }
      default: {
        return null;
      }
    }
  }

  function renderComparator() {
    if (fieldId && fieldId.startsWith("integration:")) {
      const field = getField(fieldId);
      const labels = JSON.parse(field?.settings_str ?? "{}").labels as Record<string, string> | undefined;

      if (labels) {
        return (
          <>
            <span>is</span>
            <RuleEditableDropdown
              value={value}
              placeholder="Value"
              name="value"
              options={Object.values(labels)}
              getTitle={(label) => label}
              onChange={(label) => {
                setComparator(ElementRuleComparator.eq);
                setValue(label);
              }}
              editable={isActive}
            />
          </>
        );
      }
    }

    return (
      <>
        {shouldRenderIs(comparator) && <span>is</span>}
        <RuleEditableDropdown
          value={comparator}
          placeholder="Condition"
          name="condition"
          options={Object.values(ElementRuleComparator)}
          getTitle={getComparatorTitle}
          onChange={setComparator}
          editable={isActive}
        />
        {shouldRenderTo(comparator) && <span>to</span>}
        <RuleEditableText value={value} placeholder="Value" name="value" onChange={setValue} editable={isActive} />
      </>
    );
  }

  return (
    <div
      className={classNames(style.ruleContainer, rule && !rule.active && style.inactive)}
      data-is-placeholder={!!isPlaceholder}
    >
      <span>If</span>
      <RuleCategorizedDropdown
        value={fieldId}
        valueMissing={missingField}
        name="field"
        placeholder="Field"
        options={fieldOptions}
        categories={fieldCategories}
        getTitle={getFieldTitle}
        onChange={(newFieldId) => {
          setFieldId(newFieldId);
          if (newFieldId.startsWith("integration:")) {
            // Remove :columnId from the newFieldId. (take all subsegments except the last one)
            setItemId(newFieldId.split(":").slice(0, -1).join(":"));
          } else {
            setItemId("customFields");
          }
          if (rule?.fieldId !== newFieldId) {
            setValue("");
          }
        }}
        editable={isActive}
      />
      {renderComparator()}
      <span>change</span>
      <RuleEditableDropdown
        value={actionProperty}
        options={actionProperties}
        placeholder="Property"
        name="property"
        getTitle={getActionPropertyTitle}
        onChange={(property) => {
          setActionProperty(property);
          setActionValue(rule?.action.value);
        }}
        editable={isActive}
      />
      {renderActionValueInput()}
      {missingField && (
        <div className={style.missingField}>
          <MissingDataIcon />
          <span>Missing column</span>
        </div>
      )}
      {menuOptions && (
        <div
          ref={menuRef}
          className={classNames(style.menuButton, { [style.menuButtonActive]: showMenu })}
          onClick={() => {
            setShowMenu(!showMenu);
          }}
        >
          <ThreeDotsIcon width={14} height={"100%"} />
        </div>
      )}
      {showMenu && menuOptions && (
        <Submenu relativeTo={menuRef} options={menuOptions} closeMenu={() => setShowMenu(false)} />
      )}
    </div>
  );
}

function Submenu({
  relativeTo,
  options,
  closeMenu,
}: {
  relativeTo: React.RefObject<HTMLElement>;
  options: RuleEditorSubmenuOption[];
  closeMenu: () => void;
}) {
  const ref = useRef(null);
  useOnClickOutside(ref, closeMenu, "mouseup");
  const portal = document.getElementById(PortalID.Floaters);

  const element = relativeTo.current ? (
    <Floater
      ref={ref}
      relativeTo={relativeTo}
      side={"bottom"}
      margin={16}
      arrowSize={0}
      boundary={"no-controls-area"}
      extraStyle={{ marginTop: "-3px", zIndex: 11 }}
    >
      <div className={style.ruleSubmenu}>
        {options.map((option, index) => (
          <SubmenuOption key={index} option={option} />
        ))}
      </div>
    </Floater>
  ) : null;

  return portal ? ReactDOM.createPortal(element, portal) : element;
}

function SubmenuOption({ option }: { option: RuleEditorSubmenuOption }) {
  return (
    <div
      className={style.submenuOption}
      data-testid={`rule-cell-submenu-${option.id}`}
      onClick={option.onClick}
      data-id={option.id}
    >
      <div className={style.optionIcon}>{option.icon}</div>
      <span className={style.optionTitle}>{option.title}</span>
    </div>
  );
}
